import { Injectable, NgZone } from '@angular/core';
import { ModalController } from 'ionic-angular';
import { AppData } from '../providers/app-data';
import { EsriObjectData } from '../providers/esri-object-data';
import { IdentifyData } from '../providers/identify-data';
import { LayerData } from '../providers/layer-data';
import { SearchData } from '../providers/search-data';
import { SymbolData } from '../providers/symbol-data';
import { ToastData } from '../providers/toast-data';
import { Utilities } from '../providers/utilities';
import { FeatureLayerDefinition, Point, PointSettings } from '../interfaces';

@Injectable()
export class MapData {

	EsriMap:any;
	EsriMapView:any;
	dynamicMapServiceLayer:any;
	esriObjects:any;
	esriMapUpdating: boolean = false;
	identifyGraphicsLayer: any;
	mapUpatingChangeTimeout: any;
	scaleChangeTimeout: any;
	searchLocationLayer: any;
	positionLayer: any;
	mapClickLayer: any;
	identifyTask: any;
	identifyParams: any;
	previousScale: any;
	currentScale = 0;
	scaleThreshold: 0;
	scaleIsChanging: boolean = false;
	appSettings: any;
	toastVisible: boolean = false;

	constructor(private esriObjectData: EsriObjectData, public modalCtrl: ModalController, private appData: AppData,
        private identifyData: IdentifyData, private layerData: LayerData, private searchData: SearchData, private symbolData: SymbolData, private toastData: ToastData, private utilities: Utilities, private readonly zone: NgZone) {
		this.appSettings = this.appData.settings;
		this.scaleThreshold = this.appSettings.scaleThreshold;
	}
	
	async initializeMap(mapElement) {
		let result = await this.esriObjectData.loadEsriObjectsAsync();

				this.esriObjects = result;

				this.esriObjects.urlUtils.addProxyRule({
					urlPrefix: '',
					proxyUrl: this.appData.settings.proxyUrl
				});

				let startingPosition = this.appData.getCurrentPositionOrDefault();
                this.zone.runOutsideAngular(() => {
                    this.EsriMap = new this.esriObjects.Map({
                        basemap: this.appSettings.defaultBasemap
                    });

                    this.EsriMapView = new this.esriObjects.MapView({
                        container: "viewDiv",
                        map:this.EsriMap,
                        zoom: this.appSettings.startingZoom,
                        center: [startingPosition.x, startingPosition.y],
                        constraints: {
                            rotationEnabled: this.appSettings.rotationEnabled
                        }
                    });
                });
				this.EsriMapView.ui.components.length = 0;//ESRI recommends to run this for mobile

				this.currentScale = this.EsriMapView.scale;

				this.EsriMapView.watch('scale', ()=>{
					this.scaleIsChanging = true;
					if (this.scaleChangeTimeout) {
						clearTimeout(this.scaleChangeTimeout);
					}
					this.scaleChangeTimeout = setTimeout(()=>{
						this.scaleIsChanging = false}, this.appSettings.scaleChangeBufferTimeBeforeIdentify);
					this.previousScale = this.currentScale;
					this.currentScale = this.EsriMapView.scale;
					if (this.appSettings.showZoomInWarning) {
						if (this.currentScale > this.scaleThreshold && this.previousScale <= this.scaleThreshold) {
							this.toastData.presentMessage('Zoom in to see layer(s).');
							this.toastVisible = true;
						}
						if (this.currentScale <= this.scaleThreshold && this.toastVisible) {
							this.toastData.clearMessage();
							this.toastVisible = false;
						}
					}
				});

				this.EsriMapView.watch('updating', ()=>{
					this.utilities.addMapLoadedClassToBody();
					this.esriMapUpdating = this.EsriMapView.updating;
					if (this.mapUpatingChangeTimeout) {
						clearTimeout(this.mapUpatingChangeTimeout);
					}
					this.mapUpatingChangeTimeout = setTimeout(()=> {
						this.esriMapUpdating = false;}, this.appSettings.mapUpdatingTimeLimit);
				});

				let mapServerUrl = this.appSettings.mapServerUrl;
				let subLayers = this.layerData.getSublayers();

				//Add a MapImageLayer if sublayers exist
				if (subLayers.length > 0) {
					this.dynamicMapServiceLayer = new this.esriObjects.MapImageLayer({
							'url': mapServerUrl,
							'sublayers': subLayers
						}
					);
					let dynamicMapServiceLayer = this.dynamicMapServiceLayer;
					dynamicMapServiceLayer.minScale = this.scaleThreshold;
					this.EsriMap.add(dynamicMapServiceLayer);
				}
				this.appData.CombinedFeatureLayers.forEach((fl)=> {
					let featureLayer = new this.esriObjects.FeatureLayer({
						url: fl.mapServerUrl + '/' + fl.layerNumber,
						outFields: this.appData.getOutFieldsFromLayerDefinition(fl),
						renderer: fl.renderer,
						minScale: this.scaleThreshold,
						visible: fl.visible,
						definitionExpression: fl.definitionExpression
					});
					this.EsriMap.add(featureLayer);
					fl.id = featureLayer.id;
				});

				this.positionLayer = new this.esriObjects.GraphicsLayer();
				this.EsriMap.add(this.positionLayer);
				this.utilities.addLayerClass(this.positionLayer, this.EsriMap.layers.length + 2, 'pulse');

				this.searchLocationLayer = new this.esriObjects.GraphicsLayer();
				this.EsriMap.add(this.searchLocationLayer);

				this.identifyGraphicsLayer = new this.esriObjects.GraphicsLayer();
				this.identifyGraphicsLayer.minScale = this.scaleThreshold;
				this.EsriMap.add(this.identifyGraphicsLayer);

				this.mapClickLayer = new this.esriObjects.GraphicsLayer();
				this.EsriMap.add(this.mapClickLayer);
				this.utilities.addLayerClass(this.mapClickLayer, this.EsriMap.layers.length + 2, 'fadeout');

				if (this.dynamicMapServiceLayer) {
					this.identifyTask = new this.esriObjects.IdentifyTask({'url': mapServerUrl});
					this.identifyParams = new this.esriObjects.IdentifyParameters();
					this.identifyParams.layerOption = 'all';
					this.identifyParams.width = this.EsriMapView.width;
					this.identifyParams.height = this.EsriMapView.height;
				}

				this.EsriMapView.on('click', (evt) => {
					this.handleMapClickForIdentify(evt);
				});

				this.EsriMapView.on('double-click', (evt) => {
					this.handleMapClickForIdentify(evt);
				});

				// //Update position marker when position changes
				this.appData.devicePositionSubject.subscribe((p)=> {
					this.refreshCurrentPositionMarker();
				});
				return this.EsriMap;
	}

	dismissIdentify() {
		this.identifyData.dismissModal();
		this.identifyGraphicsLayer.removeAll();
	}

	handleMapClickForIdentify(evt) {
		if (!this.scaleIsChanging) {
			this.searchData.searchMode = false;
			this.showMapClickPositionOnMap(evt.mapPoint)
			this.utilities.addModalClassToBody();
			if (this.dynamicMapServiceLayer) {
				if (this.currentScale <= this.scaleThreshold) {
					this.identifyParams.layerIds = this.getVisibleLayerIds();
					this.identifyParams.extent = this.EsriMapView.extent;
					this.identifyParams.event = evt;
					this.identifyData.executeIdentifyTask(this.identifyParams, this.identifyTask).subscribe(result => {
						this.showFeaturesOnMap();
					});
				}
			} else {
				this.runHitTest(evt);
			}
		}
	}

	runHitTest(evt: any) {
		this.appData.selectedFeature = null;
		this.identifyData.executeHitTest(this.EsriMapView, evt).then(() => {
			this.showFeaturesOnMap();
		});
	}

	showFeaturesOnMap() {
		this.identifyGraphicsLayer.removeAll();
		if (!this.appData.selectedFeature || !this.appData.selectedFeature.geometry) {
			this.dismissIdentify();
			return;
		}
		this.identifyData.presentModal();
		var identifyColor = this.appSettings.identify.color;
		var geometries = [this.appData.selectedFeature.geometry];
		geometries.forEach((geometry) => {
			var graphic = this.symbolData.getGraphicFromGeometry(geometry, identifyColor);
			this.identifyGraphicsLayer.add(graphic);
		});
	}

	showRefreshIcon() {
		let show: boolean = this.scaleIsChanging || this.esriMapUpdating;
		return show && (this.currentScale <= this.scaleThreshold);
	}

	getCurrentPositionPoint() {
		if (this.appData.devicePosition) {
			let coords = this.appData.devicePosition.coords;
			return this.esriObjectData.getGeographicPoint(coords.longitude, coords.latitude);
		}
		return null;
	}

	refreshCurrentPositionMarker() {
		let point  = this.getCurrentPositionPoint();
		this.refreshPointLayer(this.positionLayer, point, this.appSettings.currentPosition);
	}

	zoomToCurrentLocation() {
		this.appData.resetDevicePosition().then((result) => {
			let point  = this.getCurrentPositionPoint();
			if (point) {
				this.zoomMapToPoint(point, 16);
			}
		}, (error) => {
			if (this.appData.positionErrorText) {
				this.toastData.presentMessage(this.appData.positionErrorText);
			}
		});
	}

	showSearchLocationOnMap(point) {
		let searchSettings = this.appSettings.searchPosition;
		this.refreshPointLayer(this.searchLocationLayer, point, searchSettings);
		this.zoomMapToPoint(point, this.appSettings.zoomToAddressZoomLevel);
	}

	showMapClickPositionOnMap(point) {
		this.utilities.resetLayerClass(99, 'fadeout');//99 is the max number of indexes to search
		this.appSettings.mapClickPosition.size = 2 + (4 * this.appSettings.hitTestFactor);
		this.refreshPointLayer(this.mapClickLayer, point, this.appSettings.mapClickPosition);
	}

	refreshPointLayer(layer, point, pointSettings: PointSettings) {
		layer.removeAll();
		if (point) {
			var graphic = this.symbolData.getPointGraphic(point, pointSettings.color, pointSettings.size , pointSettings.outlineColor, pointSettings.outlineWidth);
			layer.add(graphic);
		}
	}
	
	setBasemap() {
		this.EsriMap.basemap = this.appData.selectedBasemap;
	}

	zoomMapToPoint(point: Point, zoom: number, forceZoom?: boolean): void {
		if (this.EsriMap != null && this.esriObjects && point.spatialReference) {
			var currentZoom = this.EsriMapView.zoom;
			if (currentZoom < zoom || forceZoom) {
				this.EsriMapView.zoom = zoom;
			}
			if (point.spatialReference.isGeographic) {
				this.EsriMapView.center = [point.x, point.y];
			} else {
				this.EsriMapView.center = point;
			}
		}
	}

	isLayerVisible(layer:any): boolean {
		if (layer) {
			return layer.visible;
		}
		return false;
		//if (this.getSubLayers().length > layer.layerId) {
		//	return this.getSubLayers()[layer.layerId].visible;
		//}
		//return false;
	}

	toggleDynamicLayerVisibility(layer): void {
		layer.visible = !layer.visible;
		//let matchingLayer = this.getSubLayers()[layer.layerId];
		//matchingLayer.visible = !matchingLayer.visible;
	}

	toggleFeatureLayerVisibility(layer: FeatureLayerDefinition): void {
		layer.visible = !layer.visible;
		this.setFeatureLayerVisibility(layer);
	}

	setFeatureLayerVisibility(layer: FeatureLayerDefinition) {
		let matchingLayer = this.EsriMap.findLayerById(layer.id);
		matchingLayer.visible = layer.visible;
	}

	refreshRoadwayLayerVisibility() {
		this.appData.RoadLayers.forEach((layer)=> {
			this.setFeatureLayerVisibility(layer);
		});
	}

	getVisibleLayerIds(): Array<number> {
		var indexes = [];
		let subLayers = this.getSubLayers()
		for (var i = 0; i < subLayers.length; i++) {
			if (subLayers[i].visible) {
				indexes.push(i);
			}
		}
		return indexes;
	}

	getSubLayers() {
		let dynamicLayerIndex = 0;
		if (this.EsriMapView.layerViews.items[dynamicLayerIndex].layer.allSublayers) {
			return this.EsriMapView.layerViews.items[dynamicLayerIndex].layer.allSublayers.items;
		}
		return [];
	}

}


// WEBPACK FOOTER //
// ./src/providers/map-data.ts